import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setActivePage } from "../store/activePage";
import DataTable from "react-data-table-component";
import GetCustomerList from "../hooks/handleGetCustomerList";

function Avatar({ name }) {
  const initials = name
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const colors = [
    "bg-emerald-100 text-emerald-700",
    "bg-violet-100 text-violet-700",
    "bg-sky-100 text-sky-700",
    "bg-amber-100 text-amber-700",
    "bg-rose-100 text-rose-700",
  ];
  const color = colors[name.charCodeAt(0) % colors.length];

  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${color}`}
    >
      {initials}
    </div>
  );
}

function EmptyState({ search }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-slate-500">
      <span className="mb-4 text-3xl font-black">{search ? "SR" : "CU"}</span>
      <p className="text-sm font-semibold text-slate-600">
        {search ? `No results for "${search}"` : "No customers yet"}
      </p>
      <p className="mt-1 text-xs text-slate-400">
        {search ? "Try a different search term" : "Customers will appear here"}
      </p>
    </div>
  );
}

function SearchBar({ value, onChange }) {
  return (
    <div className="relative">
      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">
        S
      </span>
      <input
        type="text"
        placeholder="Search by name, mobile, email or ID..."
        value={value}
        onChange={onChange}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
      />
      {value && (
        <button
          onClick={() => onChange({ target: { value: "" } })}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-lg leading-none text-slate-400 hover:text-slate-600"
        >
          x
        </button>
      )}
    </div>
  );
}

const tableStyles = {
  table: {
    style: {
      backgroundColor: "transparent",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f4f8fb",
      borderBottom: "1px solid #dbe5ec",
      borderRadius: "0",
    },
  },
  headCells: {
    style: {
      color: "#64748b",
      fontSize: "11px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      backgroundColor: "transparent",
      borderBottom: "1px solid #e5edf3",
      color: "#1f2937",
      fontSize: "13px",
      "&:hover": {
        backgroundColor: "#f8fbfd",
        cursor: "pointer",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
      paddingTop: "12px",
      paddingBottom: "12px",
    },
  },
  pagination: {
    style: {
      backgroundColor: "#ffffff",
      borderTop: "1px solid #e5edf3",
      color: "#64748b",
      fontSize: "13px",
    },
    pageButtonsStyle: {
      color: "#64748b",
      fill: "#64748b",
      "&:hover:not(:disabled)": {
        backgroundColor: "#eef7f8",
      },
      "&:disabled": {
        opacity: 0.3,
      },
    },
  },
  noData: {
    style: {
      backgroundColor: "transparent",
      color: "#64748b",
    },
  },
};

function OperatorCustomerHistory() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [customerDetails, setCustomerDetails] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Customers List",
        activeMenu: "customerList",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      const cusDetails = await GetCustomerList(dispatch);
      setLoading(false);
      if (cusDetails?.length > 0) {
        setFilteredData(cusDetails);
        setCustomerDetails(cusDetails);
      }
    })();
  }, [dispatch]);

  const columns = [
    {
      name: "Customer",
      cell: (row) => (
        <div className="flex items-center gap-3 py-1">
          <Avatar name={`${row.first_name} ${row.last_name}`} />
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {row.first_name} {row.last_name}
            </p>
            <p className="text-xs text-slate-500">{row.email || "-"}</p>
          </div>
        </div>
      ),
      sortable: true,
      sortFunction: (a, b) => a.first_name.localeCompare(b.first_name),
      grow: 2,
    },
    {
      name: "Mobile",
      selector: (row) => row.mobile,
      cell: (row) => (
        <span className="text-sm font-mono text-slate-700">{row.mobile}</span>
      ),
      sortable: true,
    },
    {
      name: "ID",
      selector: (row) => row.user_id,
      cell: (row) => (
        <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-500">
          #{row.user_id}
        </span>
      ),
      sortable: true,
      width: "90px",
    },
  ];

  const handleSearch = (event) => {
    const searchText = event.target.value.toLowerCase();
    setSearch(searchText);
    const filtered = customerDetails.filter(
      (item) =>
        item.first_name.toLowerCase().includes(searchText) ||
        item.last_name.toLowerCase().includes(searchText) ||
        item.user_id.toString().includes(searchText) ||
        item.mobile.toString().includes(searchText) ||
        (item.email || "").toString().toLowerCase().includes(searchText),
    );
    setFilteredData(filtered);
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#edf5f8_0%,#f8fbfd_42%,#edf4f8_100%)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-4xl space-y-5">
        <div className="flex items-center justify-between rounded-3xl bg-[linear-gradient(135deg,#f8fbfd_0%,#edf7f6_50%,#d8f0ec_100%)] px-6 py-5 shadow-lg shadow-slate-200/70 ring-1 ring-white/70">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-sky-200 bg-sky-50 text-base font-black text-sky-700">
              CU
            </div>
            <div>
              <h1 className="text-lg font-black tracking-wide text-slate-900">
                Customer List
              </h1>
              <p className="mt-0.5 text-xs text-slate-500">
                {loading ? "Loading..." : `${customerDetails.length} total customers`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            <span className="text-xs font-semibold text-emerald-600">
              {filteredData.length} shown
            </span>
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-white/80 bg-white/95 shadow-xl shadow-slate-200/70">
          <div className="border-b border-slate-200 px-4 py-3">
            <SearchBar value={search} onChange={handleSearch} />
          </div>

          {loading ? (
            <div className="space-y-0">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="flex animate-pulse items-center gap-4 border-b border-slate-200 px-4 py-3.5"
                >
                  <div className="h-8 w-8 shrink-0 rounded-lg bg-slate-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-1/3 rounded bg-slate-200" />
                    <div className="h-2.5 w-1/2 rounded bg-slate-200" />
                  </div>
                  <div className="h-3 w-20 rounded bg-slate-200" />
                </div>
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              paginationPerPage={10}
              highlightOnHover
              customStyles={tableStyles}
              noDataComponent={<EmptyState search={search} />}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default OperatorCustomerHistory;
